import { Calendar, ChevronLeft, ChevronRight } from 'lucide-react';
import React, { useCallback, useEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { useTranslation } from 'react-i18next';
import useLanguageTranslate from '../../../hooks/useLanguageTranslate';
import Button from '../Button/Button';

export default function DateRangeInput({
  onDateSelect,
  initialStartDate = null,
  initialEndDate = null,
  fromPlaceholder = 'Select departure date',
  toPlaceholder = 'Select return date',
  fromLabel = 'From',
  toLabel = 'To',
  inline = true,
  disableEndDate = false,
}) {
  const translate = useLanguageTranslate();
  const { i18n } = useTranslation();
  const isRTL = i18n.resolvedLanguage === 'ar';
  const [currentDate, setCurrentDate] = useState(new Date());
  const [startDate, setStartDate] = useState(initialStartDate);
  const [endDate, setEndDate] = useState(initialEndDate);
  const [hoverDate, setHoverDate] = useState(null);
  const [isOpen, setIsOpen] = useState(false);
  const [activeField, setActiveField] = useState('from');
  const [isMobile, setIsMobile] = useState(false);
  // Popup position (relative to viewport) computed each time the calendar opens.
  // The popup is rendered into a body-level portal with position:fixed so no
  // ancestor overflow/transform can clip it.
  const [popupRect, setPopupRect] = useState(null);

  const fromInputRef = useRef(null);
  const toInputRef = useRef(null);
  const calendarRef = useRef(null);

  const monthNames = [
    translate('January', 'يناير'),
    translate('February', 'فبراير'),
    translate('March', 'مارس'),
    translate('April', 'أبريل'),
    translate('May', 'مايو'),
    translate('June', 'يونيو'),
    translate('July', 'يوليو'),
    translate('August', 'أغسطس'),
    translate('September', 'سبتمبر'),
    translate('October', 'أكتوبر'),
    translate('November', 'نوفمبر'),
    translate('December', 'ديسمبر'),
  ];

  const dayNames = [
    translate('M', 'الإثنين'), // Monday - الإثنين
    translate('T', 'الثلاثاء'), // Tuesday - الثلاثاء
    translate('W', 'الأربعاء'), // Wednesday - الأربعاء
    translate('T', 'الخميس'), // Thursday - الخميس
    translate('F', 'الجمعة'), // Friday - الجمعة
    translate('S', 'السبت'), // Saturday - السبت
    translate('S', 'الأحد'), // Sunday - الأحد
  ];

  // Compute fixed-position rect for the popup relative to the viewport.
  // The popup is portaled to body, so this also escapes any ancestor overflow.
  const computePopupRect = useCallback(() => {
    const activeInput = activeField === 'from' ? fromInputRef.current : toInputRef.current;
    if (!activeInput) return;

    const inputRect = activeInput.getBoundingClientRect();
    const viewportHeight = window.innerHeight;
    const viewportWidth = window.innerWidth;

    // Footer is rendered into a portal at the bottom of the viewport. Measure
    // its real height (incl. safe-area padding) when available.
    let footerHeight = 0;
    const footerEl = document.querySelector('.footer');
    if (footerEl) {
      const r = footerEl.getBoundingClientRect();
      if (r.height > 0) footerHeight = r.height;
    }

    const gap = 8; // matches the previous mt-2 between input and popup
    const safety = 16; // breathing room above the footer / below page top

    // Pick the side with more room so the popup is least likely to overlap
    // the footer. The popup itself is sized to its content (no inner scroll),
    // so we anchor by `top` for drop-down or `bottom` for flip-up and let the
    // calendar take its natural height.
    const spaceBelow = viewportHeight - inputRect.bottom - gap - footerHeight - safety;
    const spaceAbove = inputRect.top - gap - safety;
    const placeAbove = spaceAbove > spaceBelow;

    // Width: mobile = full viewport; desktop = at least 600px, aligned to input.
    const margin = 8;
    let left;
    let width;
    if (isMobile) {
      left = 0;
      width = viewportWidth;
    } else {
      const desiredMinWidth = 600;
      width = Math.max(desiredMinWidth, inputRect.width);
      if (isRTL) {
        // Anchor to input's right edge growing leftward
        left = inputRect.right - width;
      } else {
        left = inputRect.left;
      }
    }

    // Horizontal bounds pass — keep popup inside the viewport. Width is
    // clamped to a non-negative max so extremely narrow viewports can't
    // produce a negative width.
    const maxWidth = Math.max(1, viewportWidth - margin * 2);
    if (width > maxWidth) width = maxWidth;
    if (width < 1) width = 1;
    if (left < margin) left = margin;
    if (left + width > viewportWidth - margin) left = viewportWidth - margin - width;
    if (left < margin) left = margin;

    // Vertical anchor: drop-down anchors by top, flip-up anchors by bottom so
    // the popup grows in the right direction at its natural content height.
    if (placeAbove) {
      const bottom = Math.max(safety, viewportHeight - inputRect.top + gap);
      setPopupRect({ bottom, left, width });
    } else {
      const top = Math.max(safety, inputRect.bottom + gap);
      setPopupRect({ top, left, width });
    }
  }, [activeField, isMobile, isRTL]);

  // Check if mobile on mount and resize
  useEffect(() => {
    const checkMobile = () => {
      setIsMobile(window.innerWidth < 768);
    };

    const handleResize = () => {
      checkMobile();
      if (isOpen) computePopupRect();
    };

    checkMobile();
    window.addEventListener('resize', handleResize);

    return () => window.removeEventListener('resize', handleResize);
  }, [isOpen, computePopupRect]);

  // Close calendar when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (
        calendarRef.current &&
        !calendarRef.current.contains(event.target) &&
        fromInputRef.current &&
        !fromInputRef.current.contains(event.target) &&
        toInputRef.current &&
        !toInputRef.current.contains(event.target)
      ) {
        setIsOpen(false);
      }
    };

    if (isOpen) {
      document.addEventListener('mousedown', handleClickOutside);
    }

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [isOpen]);

  // Prevent body scroll on mobile when calendar is open
  useEffect(() => {
    if (isMobile && isOpen) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = 'unset';
    }

    return () => {
      document.body.style.overflow = 'unset';
    };
  }, [isMobile, isOpen]);

  // Recompute popup position whenever it opens or the active field changes,
  // and keep it pinned to the input on scroll/resize while open.
  useEffect(() => {
    if (!isOpen) {
      setPopupRect(null);
      return;
    }
    // Defer one frame so the input ref has a final rect after layout.
    const id = window.setTimeout(() => computePopupRect(), 0);
    const handle = () => computePopupRect();
    // Use capture=true so we catch scrolls in any ancestor scroll container.
    window.addEventListener('scroll', handle, true);
    window.addEventListener('resize', handle);
    return () => {
      window.clearTimeout(id);
      window.removeEventListener('scroll', handle, true);
      window.removeEventListener('resize', handle);
    };
  }, [isOpen, activeField, computePopupRect]);

  // Update internal state when initial date props change
  useEffect(() => {
    setStartDate(initialStartDate);
    setEndDate(initialEndDate);
  }, [initialStartDate, initialEndDate]);

  const getDaysInMonth = (date) => {
    return new Date(date.getFullYear(), date.getMonth() + 1, 0).getDate();
  };

  const getFirstDayOfMonth = (date) => {
    const firstDay = new Date(date.getFullYear(), date.getMonth(), 1).getDay();
    return firstDay === 0 ? 6 : firstDay - 1;
  };

  const isSameDay = (date1, date2) => {
    if (!date1 || !date2) return false;
    return date1.toDateString() === date2.toDateString();
  };

  const isDateInRange = (date, start, end) => {
    if (!start || !end) return false;
    return date >= start && date <= end;
  };

  const isDateInHoverRange = (date) => {
    if (!startDate || !hoverDate || endDate || isMobile) return false;
    const rangeStart = startDate < hoverDate ? startDate : hoverDate;
    const rangeEnd = startDate < hoverDate ? hoverDate : startDate;
    return date >= rangeStart && date <= rangeEnd;
  };

  const handleDateClick = (date) => {
    const today = new Date();
    today.setHours(0, 0, 0, 0);

    // Travel policies cannot be issued for same-day departure — minimum is tomorrow.
    if (date <= today) {
      return;
    }

    if (activeField === 'from') {
      setStartDate(date);
      if (endDate && date >= endDate) {
        setEndDate(null);
        onDateSelect?.(date, null);
      } else {
        onDateSelect?.(date, endDate);
      }
      if (disableEndDate) {
        setIsOpen(false);
      } else {
        setActiveField('to');
      }
    } else if (activeField === 'to') {
      if (startDate && date > startDate) {
        // Allow selection of any end date after start date
        // Validation will be handled by the parent component on form submission
        setEndDate(date);
        onDateSelect?.(startDate, date);
        setIsOpen(false);
      } else if (startDate && date <= startDate) {
        setStartDate(date);
        setEndDate(null);
        setActiveField('to');
        onDateSelect?.(date, null);
      }
    }
  };

  const handleDateHover = (date) => {
    if (startDate && !endDate && activeField === 'to' && !isMobile) {
      setHoverDate(date);
    }
  };

  const handleDateLeave = () => {
    if (!isMobile) {
      setHoverDate(null);
    }
  };

  const navigateMonth = (direction) => {
    setCurrentDate((prev) => {
      const newDate = new Date(prev);
      if (direction === 'prev') {
        newDate.setMonth(prev.getMonth() - 1);
      } else {
        newDate.setMonth(prev.getMonth() + 1);
      }
      return newDate;
    });
  };

  const handleFromClick = () => {
    setActiveField('from');
    setIsOpen(true);
  };

  const handleToClick = () => {
    setActiveField('to');
    setIsOpen(true);
  };

  const renderMonth = (monthOffset = 0) => {
    const displayDate = new Date(currentDate.getFullYear(), currentDate.getMonth() + monthOffset, 1);
    const daysInMonth = getDaysInMonth(displayDate);
    const firstDayOfMonth = getFirstDayOfMonth(displayDate);
    const today = new Date();
    today.setHours(0, 0, 0, 0);

    const days = [];

    // Empty cells for days before the first day of the month
    for (let i = 0; i < firstDayOfMonth; i++) {
      days.push(<div key={`empty-${i}`} className={`${isMobile ? 'h-8 w-8' : 'h-10 w-10'} m-0.5`} />);
    }

    // Days of the month
    for (let day = 1; day <= daysInMonth; day++) {
      const date = new Date(displayDate.getFullYear(), displayDate.getMonth(), day);
      const isInRange = isDateInRange(date, startDate, endDate);
      const isInHoverRange = isDateInHoverRange(date);
      const isPast = date <= today;
      const isStart = isSameDay(date, startDate);
      const isEnd = isSameDay(date, endDate);
      
      // Allow selection of all future dates - validation will be handled by parent component
      let isBeyondMaxDuration = false;

      let dayClasses = `${isMobile ? 'h-8 w-8 text-xs min-h-[32px] min-w-[32px]' : 'h-10 w-10 text-sm'} flex items-center justify-center font-medium cursor-pointer transition-colors relative m-0.5`;
      let customStyle = {};

      if (isPast || isBeyondMaxDuration) {
        dayClasses += ' text-gray-300 cursor-not-allowed';
      } else if (isStart) {
        dayClasses += ' bg-[#00719A] text-white';
        customStyle = {
          borderRadius: isRTL ? (isMobile ? '0 4px 4px 0' : '0 6px 6px 0') : isMobile ? '4px 0 0 4px' : '6px 0 0 6px',
          background: '#00719A',
        };
      } else if (isEnd) {
        dayClasses += ' bg-[#00719A] text-white';
        customStyle = {
          borderRadius: isRTL ? (isMobile ? '4px 0 0 4px' : '6px 0 0 6px') : isMobile ? '0 4px 4px 0' : '0 6px 6px 0',
          background: '#00719A',
        };
      } else if (isInRange || isInHoverRange) {
        dayClasses += ' bg-[#E0F2FE] text-gray-900';
      } else {
        dayClasses += ' text-[#48535B] hover:bg-gray-100';
      }

      // Remove hover effects for selected dates
      if (isStart || isEnd) {
        dayClasses = dayClasses.replace(' hover:bg-gray-100', '');
      }

      days.push(
        <div
          key={day}
          className={dayClasses}
          onClick={() => !isPast && !isBeyondMaxDuration && handleDateClick(date)}
          onMouseEnter={() => !isPast && !isBeyondMaxDuration && !isStart && !isEnd && handleDateHover(date)}
          onMouseLeave={handleDateLeave}
          style={customStyle}>
          <span className="relative z-10">{day}</span>
        </div>
      );
    }

    return (
      <div className="flex-1">
        <div className={`${isMobile ? 'text-base' : 'text-lg'} font-semibold text-gray-900 mb-4 text-center`}>
          {monthNames[displayDate.getMonth()]} {displayDate.getFullYear()}
        </div>
        <div className={`grid grid-cols-7 ${isRTL ? 'mb-0' : 'mb-2'}`}>
          {dayNames.map((day, index) => (
            <div
              key={index}
              className={`${isMobile ? 'h-8 w-8' : 'h-10 w-10'} flex items-center justify-center text-gray-500 ${isRTL ? 'text-[9px] font-medium' : 'font-medium'}`}>
              {day}
            </div>
          ))}
        </div>
        <div className="grid grid-cols-7">{days}</div>
      </div>
    );
  };

  const formatDate = (date) => {
    if (!date) return null;

    const year = date.getFullYear();
    const month = String(date.getMonth() + 1).padStart(2, '0');
    const day = String(date.getDate()).padStart(2, '0');

    // Format based on language: DD/MM/YYYY for both English and Arabic
    // but Arabic will be displayed RTL by the parent container
    return `${day}/${month}/${year}`;
  };

  // Inline style for the body-portaled, fixed-position popup. Hidden until the
  // first measurement lands to avoid a one-frame flash at (0,0). The popup
  // sizes to its content (no maxHeight, no inner scroll).
  const popupStyle = popupRect
    ? {
        position: 'fixed',
        left: `${popupRect.left}px`,
        width: `${popupRect.width}px`,
        ...(popupRect.top != null ? { top: `${popupRect.top}px` } : {}),
        ...(popupRect.bottom != null ? { bottom: `${popupRect.bottom}px` } : {}),
      }
    : { position: 'fixed', visibility: 'hidden', top: 0, left: 0 };

  // Wrap calendar contents in a body-portal so no parent overflow can clip it.
  const renderPopup = (contents) =>
    typeof document === 'undefined'
      ? null
      : createPortal(
          <div
            ref={calendarRef}
            dir={isRTL ? 'rtl' : 'ltr'}
            className="z-[9999] bg-white border border-gray-200 rounded-lg shadow-lg"
            style={popupStyle}>
            {contents}
          </div>,
          document.body
        );

  // Determine if inputs should be stacked on mobile
  const shouldStack = !inline || isMobile;

  return (
    <div className="w-full" dir={isRTL ? 'rtl' : 'ltr'}>
      {/* Input Fields Container */}
      <div className="relative">
        <div className={shouldStack ? 'space-y-4' : 'grid grid-cols-2 gap-4'}>
          {/* From Input */}
          <div className="relative">
            {fromLabel && (
              <label className="label text-[#48535B] text-base not-italic font-normal leading-[26px] tracking-[-0.16px] mb-2 text-left rtl:text-right">
                {translate(fromLabel, fromLabel)}
              </label>
            )}
            <div
              ref={fromInputRef}
              onClick={handleFromClick}
              className={`w-full h-[50px] px-4 py-3 border rounded-[10px] bg-white cursor-pointer flex items-center justify-between hover:border-gray-400 transition-colors ${
                activeField === 'from' && isOpen ? 'border-[#0891B2] ring-1 ring-[#0891B2]' : 'border-[#DDE2E4]'
              }`}>
              <span
                className={`text-lg font-normal ${startDate ? 'text-black' : 'text-gray-500'}`}>
                {formatDate(startDate) || fromPlaceholder}
              </span>
              <Calendar size={20} className="text-gray-400" />
            </div>

            {/* Calendar positioned below From input for stacked layout when from is active */}
            {isOpen && shouldStack && activeField === 'from' && renderPopup(
              <div className="bg-white border-0 shadow-none rounded-lg">
                  <div className={isMobile ? 'p-4' : 'p-8'}>
                    {/* Navigation header */}
                    <div className="flex items-center justify-between">
                      <button
                        onClick={() => navigateMonth('prev')}
                        className="p-2 bg-transparent hover:bg-gray-100 rounded-full w-10 h-10 flex items-center justify-center transition-colors">
                        {isRTL ? (
                          <ChevronRight size={20} className="text-gray-600" />
                        ) : (
                          <ChevronLeft size={20} className="text-gray-600" />
                        )}
                      </button>

                      <div className="text-lg font-semibold text-gray-900"></div>

                      <button
                        onClick={() => navigateMonth('next')}
                        className="p-2 bg-transparent hover:bg-gray-100 rounded-full w-10 h-10 flex items-center justify-center transition-colors">
                        {isRTL ? (
                          <ChevronLeft size={20} className="text-gray-600" />
                        ) : (
                          <ChevronRight size={20} className="text-gray-600" />
                        )}
                      </button>
                    </div>

                    <div className={`flex gap-12 ${isMobile ? 'flex-col space-y-6' : 'flex-row'}`}>
                      {isRTL && !isMobile ? (
                        <>
                          {renderMonth(1)}
                          {renderMonth(0)}
                        </>
                      ) : (
                        <>
                          {renderMonth(0)}
                          {!isMobile && renderMonth(1)}
                        </>
                      )}
                    </div>
                    <hr />
                    <div className={`flex justify-end ${isMobile ? 'mt-2' : 'mt-4'}`}>
                      <Button
                        onSubmit={() => setIsOpen(false)}
                        disabled={!startDate || !endDate}
                        label={translate('Done', 'تم')}
                        className="!bg-[#0891B2] hover:!bg-[#0891B2]/90 !text-white !px-8 !py-2 !rounded-lg !font-normal disabled:!opacity-50"
                      />
                    </div>
                  </div>
                </div>
            )}
          </div>

          {/* To Input */}
          <div className="relative">
            {toLabel && (
              <label className="label text-[#48535B] text-base not-italic font-normal leading-[26px] tracking-[-0.16px] mb-2 text-left rtl:text-right">
                {translate(toLabel, toLabel)}
              </label>
            )}
            <div
              ref={toInputRef}
              onClick={disableEndDate ? undefined : handleToClick}
              className={`w-full h-[50px] px-4 py-3 border rounded-[10px] flex items-center justify-between transition-colors ${
                disableEndDate
                  ? 'bg-gray-100 cursor-not-allowed border-[#DDE2E4]'
                  : `bg-white cursor-pointer hover:border-gray-400 ${activeField === 'to' && isOpen ? 'border-[#0891B2] ring-1 ring-[#0891B2]' : 'border-[#DDE2E4]'}`
              }`}>
              <span
                className={`text-lg font-normal ${endDate ? 'text-black' : 'text-gray-500'}`}>
                {formatDate(endDate) || toPlaceholder}
              </span>
              <Calendar size={20} className="text-gray-400" />
            </div>

            {/* Calendar positioned below To input for stacked layout when to is active */}
            {isOpen && shouldStack && activeField === 'to' && renderPopup(
              <div className="bg-white border-0 shadow-none rounded-lg">
                  <div className={isMobile ? 'p-4' : 'p-8'}>
                    {/* Navigation header */}
                    <div className="flex items-center justify-between mb-4">
                      <button
                        onClick={() => navigateMonth('prev')}
                        className="p-2 bg-transparent hover:bg-gray-100 rounded-full w-10 h-10 flex items-center justify-center transition-colors">
                        {isRTL ? (
                          <ChevronRight size={20} className="text-gray-600" />
                        ) : (
                          <ChevronLeft size={20} className="text-gray-600" />
                        )}
                      </button>

                      <div className="text-lg font-semibold text-gray-900"></div>

                      <button
                        onClick={() => navigateMonth('next')}
                        className="p-2 bg-transparent hover:bg-gray-100 rounded-full w-10 h-10 flex items-center justify-center transition-colors">
                        {isRTL ? (
                          <ChevronLeft size={20} className="text-gray-600" />
                        ) : (
                          <ChevronRight size={20} className="text-gray-600" />
                        )}
                      </button>
                    </div>

                    <div className={`flex gap-12 ${isMobile ? 'flex-col space-y-6' : 'flex-row'}`}>
                      {isRTL && !isMobile ? (
                        <>
                          {renderMonth(1)}
                          {renderMonth(0)}
                        </>
                      ) : (
                        <>
                          {renderMonth(0)}
                          {!isMobile && renderMonth(1)}
                        </>
                      )}
                    </div>
                    <div className={`flex justify-end ${isMobile ? 'mt-6' : 'mt-8'}`}>
                      <Button
                        onSubmit={() => setIsOpen(false)}
                        disabled={!startDate || !endDate}
                        label={translate('Done', 'تم')}
                        className="!bg-[#0891B2] hover:!bg-[#0891B2]/90 !text-white !px-8 !py-2 !rounded-lg !font-medium disabled:!opacity-50"
                      />
                    </div>
                  </div>
                </div>
            )}
          </div>
        </div>

        {/* Calendar for inline layout on desktop */}
        {isOpen && !shouldStack && renderPopup(
          <div className="bg-white border-0 shadow-none rounded-lg">
              <div className="p-8">
                {/* Navigation buttons */}
                <div className="flex items-center justify-between mb-6">
                  <button
                    onClick={() => navigateMonth('prev')}
                    className="p-2 bg-transparent hover:bg-gray-100 rounded-full w-10 h-10 flex items-center justify-center transition-colors">
                    {isRTL ? (
                      <ChevronRight size={20} className="text-gray-600" />
                    ) : (
                      <ChevronLeft size={20} className="text-gray-600" />
                    )}
                  </button>

                  <div className="text-lg font-semibold text-gray-900"></div>

                  <button
                    onClick={() => navigateMonth('next')}
                    className="p-2 bg-transparent hover:bg-gray-100 rounded-full w-10 h-10 flex items-center justify-center transition-colors">
                    {isRTL ? (
                      <ChevronLeft size={20} className="text-gray-600" />
                    ) : (
                      <ChevronRight size={20} className="text-gray-600" />
                    )}
                  </button>
                </div>

                <div className={`flex gap-12 ${isRTL ? 'flex-row-reverse' : ''}`}>
                  {renderMonth(0)}
                  {renderMonth(1)}
                </div>

                <div className="flex justify-end mt-8">
                  <Button
                    onSubmit={() => setIsOpen(false)}
                    disabled={!startDate || !endDate}
                    label="Done"
                    className="!bg-[#0891B2] hover:!bg-[#0891B2]/90 !text-white !px-8 !py-2 !rounded-lg !font-normal disabled:!opacity-50"
                  />
                </div>
              </div>
            </div>
        )}
      </div>
    </div>
  );
}
